import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getPermanentTeethInRange,
  getPrimaryTeethInRange,
  PERMANENT_TEETH,
  PRIMARY_TEETH,
  ToothDentition,
} from "../../utils/toothSelectionConstants";
import {
  appendSelectionValues,
  toggleSelectionValue,
} from "../../utils/selectionValueUtils";
import {
  SelectionModalTabs,
  SelectionModalTab,
  SelectionModalSection,
  SelectionSectionHeader,
  SelectionSectionHeaderTitle,
  SelectionSectionHeaderOption,
  SelectionSectionHeaderSeparator,
  DentitionToggle,
  SelectionModalSectionBody,
  SelectionModalSectionBodySpaced,
  ToothQuadrantRow,
  ToothQuadrant,
  ToothQuadrantLabel,
  ToothButtonsRow,
  SelectionOptionButton,
  CustomInputRow,
  CustomInput,
  CustomAddButton,
  RangeFieldsRow,
  RangeField,
  RangeFieldLabel,
  SelectionTextInput,
  FullWidthActionButton,
} from "../SelectionModal/SelectionModal.styled";

type ToothSelectionTab = "single" | "range";
type ToothQuadrantPosition =
  | "upperRight"
  | "upperLeft"
  | "lowerRight"
  | "lowerLeft";

interface ToothSelectionPanelProps {
  value: string[];
  onChange: (value: string[]) => void;
  maxValues?: number;
}

const ToothSelectionPanel: React.FC<ToothSelectionPanelProps> = ({
  value,
  onChange,
  maxValues,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ToothSelectionTab>("single");
  const [dentition, setDentition] = useState<ToothDentition>("permanent");
  const [customValue, setCustomValue] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const handleToggle = (tooth: string | number) => {
    onChange(toggleSelectionValue(value, String(tooth), undefined, maxValues));
  };

  const handleCustomAdd = () => {
    if (!customValue.trim()) return;
    onChange(
      appendSelectionValues(
        value,
        [customValue],
        (item) => item.toUpperCase(),
        maxValues,
      ),
    );
    setCustomValue("");
  };

  const handleRangeAdd = () => {
    const rangeValues =
      dentition === "permanent"
        ? getPermanentTeethInRange(rangeFrom, rangeTo)
        : getPrimaryTeethInRange(rangeFrom, rangeTo);

    if (rangeValues.length === 0) return;
    onChange(appendSelectionValues(value, rangeValues, undefined, maxValues));
    setRangeFrom("");
    setRangeTo("");
  };

  const renderQuadrant = (
    label: string,
    teeth: readonly (string | number)[],
    position: ToothQuadrantPosition,
  ) => {
    const isLower = position === "lowerRight" || position === "lowerLeft";
    const align =
      position === "upperRight" || position === "lowerRight" ? "left" : "right";

    const buttons = (
      <ToothButtonsRow>
        {teeth.map((tooth) => {
          const toothValue = String(tooth);
          return (
            <SelectionOptionButton
              key={toothValue}
              type="button"
              selected={value.includes(toothValue)}
              onClick={() => handleToggle(tooth)}
            >
              {toothValue}
            </SelectionOptionButton>
          );
        })}
      </ToothButtonsRow>
    );

    const labelElement = (
      <ToothQuadrantLabel align={align}>{label}</ToothQuadrantLabel>
    );

    return (
      <ToothQuadrant quadrantPosition={position}>
        {isLower ? (
          <>
            {buttons}
            {labelElement}
          </>
        ) : (
          <>
            {labelElement}
            {buttons}
          </>
        )}
      </ToothQuadrant>
    );
  };

  const renderDentitionHeader = () => (
    <DentitionToggle>
      <SelectionSectionHeaderOption
        type="button"
        active={dentition === "permanent"}
        onClick={() => setDentition("permanent")}
      >
        {t("treatmentPlan.toothPermanent")}
      </SelectionSectionHeaderOption>
      <SelectionSectionHeaderSeparator>/</SelectionSectionHeaderSeparator>
      <SelectionSectionHeaderOption
        type="button"
        active={dentition === "primary"}
        onClick={() => setDentition("primary")}
      >
        {t("treatmentPlan.toothPrimary")}
      </SelectionSectionHeaderOption>
    </DentitionToggle>
  );

  return (
    <>
      <SelectionModalTabs>
        <SelectionModalTab
          type="button"
          active={activeTab === "single"}
          onClick={() => setActiveTab("single")}
        >
          {t("treatmentPlan.toothTabSingle")}
        </SelectionModalTab>
        <SelectionModalTab
          type="button"
          active={activeTab === "range"}
          onClick={() => setActiveTab("range")}
        >
          {t("treatmentPlan.toothTabRange")}
        </SelectionModalTab>
      </SelectionModalTabs>

      {activeTab === "single" ? (
        <>
          {renderDentitionHeader()}

          <SelectionModalSectionBodySpaced>
            {dentition === "permanent" ? (
              <>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperRight"),
                    PERMANENT_TEETH.upperRight,
                    "upperRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperLeft"),
                    PERMANENT_TEETH.upperLeft,
                    "upperLeft",
                  )}
                </ToothQuadrantRow>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerRight"),
                    PERMANENT_TEETH.lowerRight,
                    "lowerRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerLeft"),
                    PERMANENT_TEETH.lowerLeft,
                    "lowerLeft",
                  )}
                </ToothQuadrantRow>
              </>
            ) : (
              <>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperRight"),
                    PRIMARY_TEETH.upperRight,
                    "upperRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperLeft"),
                    PRIMARY_TEETH.upperLeft,
                    "upperLeft",
                  )}
                </ToothQuadrantRow>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerRight"),
                    PRIMARY_TEETH.lowerRight,
                    "lowerRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerLeft"),
                    PRIMARY_TEETH.lowerLeft,
                    "lowerLeft",
                  )}
                </ToothQuadrantRow>
              </>
            )}
          </SelectionModalSectionBodySpaced>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.customNumber")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
              <CustomInputRow>
                <CustomInput
                  value={customValue}
                  placeholder={t("treatmentPlan.enterPlaceholder")}
                  onChange={(event) => setCustomValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCustomAdd();
                    }
                  }}
                />
                <CustomAddButton type="button" onClick={handleCustomAdd}>
                  {t("treatmentPlan.add")}
                </CustomAddButton>
              </CustomInputRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>
        </>
      ) : (
        <SelectionModalSectionBody>
            <RangeFieldsRow>
              <RangeField>
                <RangeFieldLabel>{t("treatmentPlan.from")}</RangeFieldLabel>
                <SelectionTextInput
                  value={rangeFrom}
                  placeholder={t("treatmentPlan.enterPlaceholder")}
                  onChange={(event) => setRangeFrom(event.target.value)}
                />
              </RangeField>
              <RangeField>
                <RangeFieldLabel>{t("treatmentPlan.to")}</RangeFieldLabel>
                <SelectionTextInput
                  value={rangeTo}
                  placeholder={t("treatmentPlan.enterPlaceholder")}
                  onChange={(event) => setRangeTo(event.target.value)}
                />
              </RangeField>
            </RangeFieldsRow>

            <FullWidthActionButton type="button" onClick={handleRangeAdd}>
              {t("treatmentPlan.addRange")}
            </FullWidthActionButton>
        </SelectionModalSectionBody>
      )}
    </>
  );
};

export default ToothSelectionPanel;
